/**
 * <bbq-mega-menu> — desktop mega menu for sections/bbq-header.liquid (PLAN-THEME-FIXES.md WP1).
 *
 * Contract with the markup (snippets/bbq-mega-menu.liquid):
 *   [data-bbq-mega-item]     one <li> per top-level link
 *   [data-bbq-mega-trigger]  the disclosure <button> (aria-expanded, aria-controls)
 *   [data-bbq-mega-panel]    the panel <div>, closed = `hidden` + `inert`, both toggled here
 *
 * Rules:
 *   - one panel open at a time
 *   - hover-intent (OPEN_DELAY) opens, leaving the whole menu (CLOSE_DELAY) closes
 *   - the button toggles; Escape closes and returns focus to the button
 *   - focus moving outside the open item closes it; click outside closes it
 *   - no aria-selected anywhere; hidden/inert are the only closed-state signals
 *
 * It carries ref="headerMenu" for Horizon's HeaderComponent (assets/header.js:28), which only
 * checks the ref exists.
 */

const OPEN_DELAY = 120;
const CLOSE_DELAY = 180;

class BbqMegaMenu extends HTMLElement {
  /** @type {HTMLElement | null} */
  #openItem = null;
  /** @type {number | null} */
  #openTimer = null;
  /** @type {number | null} */
  #closeTimer = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('pointerover', this.#onPointerOver);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('focusin', this.#onFocusIn);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('focusin', this.#onFocusIn);
    this.#clearTimers();
  }

  #clearTimers() {
    if (this.#openTimer !== null) { clearTimeout(this.#openTimer); this.#openTimer = null; }
    if (this.#closeTimer !== null) { clearTimeout(this.#closeTimer); this.#closeTimer = null; }
  }

  /** @param {HTMLElement} item */
  #open(item) {
    if (this.#openItem === item) return;
    this.#close();
    const trigger = item.querySelector('[data-bbq-mega-trigger]');
    const panel = item.querySelector('[data-bbq-mega-panel]');
    if (!(trigger instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;
    panel.hidden = false;
    panel.inert = false;
    trigger.setAttribute('aria-expanded', 'true');
    item.setAttribute('data-open', '');
    this.#openItem = item;
  }

  #close({ returnFocus = false } = {}) {
    const item = this.#openItem;
    if (!item) return;
    const trigger = item.querySelector('[data-bbq-mega-trigger]');
    const panel = item.querySelector('[data-bbq-mega-panel]');
    if (panel instanceof HTMLElement) { panel.hidden = true; panel.inert = true; }
    if (trigger instanceof HTMLElement) {
      trigger.setAttribute('aria-expanded', 'false');
      if (returnFocus) trigger.focus();
    }
    item.removeAttribute('data-open');
    this.#openItem = null;
  }

  /** @param {Event} event */
  #itemFor(event) {
    const target = event.target;
    if (!(target instanceof Element)) return null;
    const item = target.closest('[data-bbq-mega-item]');
    return item instanceof HTMLElement && this.contains(item) ? item : null;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const trigger = target.closest('[data-bbq-mega-trigger]');
    if (!(trigger instanceof HTMLElement)) return;
    const item = this.#itemFor(event);
    if (!item || !item.querySelector('[data-bbq-mega-panel]')) return;
    event.preventDefault();
    this.#clearTimers();
    if (this.#openItem === item) this.#close({ returnFocus: true });
    else this.#open(item);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#openItem) return;
    event.preventDefault();
    this.#clearTimers();
    this.#close({ returnFocus: true });
  };

  /** @param {PointerEvent} event */
  #onPointerOver = (event) => {
    if (event.pointerType === 'touch') return;
    const item = this.#itemFor(event);
    if (this.#closeTimer !== null) { clearTimeout(this.#closeTimer); this.#closeTimer = null; }
    if (!item || !item.classList.contains('bbq-mega__item--has-panel')) {
      // Hovering a top-level item with no panel, or the gaps between items: close after a beat.
      if (this.#openItem && item !== this.#openItem) this.#scheduleClose();
      return;
    }
    if (item === this.#openItem) return;
    if (this.#openTimer !== null) clearTimeout(this.#openTimer);
    this.#openTimer = window.setTimeout(() => { this.#openTimer = null; this.#open(item); }, OPEN_DELAY);
  };

  /** @param {PointerEvent} event */
  #onPointerLeave = (event) => {
    if (event.pointerType === 'touch') return;
    if (this.#openTimer !== null) { clearTimeout(this.#openTimer); this.#openTimer = null; }
    this.#scheduleClose();
  };

  #scheduleClose() {
    if (this.#closeTimer !== null) clearTimeout(this.#closeTimer);
    this.#closeTimer = window.setTimeout(() => {
      this.#closeTimer = null;
      // Keep it open while keyboard focus is inside the open item.
      if (this.#openItem && this.#openItem.contains(document.activeElement)) return;
      this.#close();
    }, CLOSE_DELAY);
  }

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!this.#openItem) return;
    const target = event.target;
    if (target instanceof Node && this.#openItem.contains(target)) return;
    this.#clearTimers();
    this.#close();
  };

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    if (!this.#openItem) return;
    const target = event.target;
    if (target instanceof Node && this.#openItem.contains(target)) return;
    this.#clearTimers();
    this.#close();
  };
}

if (!customElements.get('bbq-mega-menu')) customElements.define('bbq-mega-menu', BbqMegaMenu);
